/**
 * 2D plan view for one storey: space footprints from the ModelSummary/API
 * polygons rendered as SVG, color-coded by effective occupancy, optionally
 * tinted by fire-alarm zone, with generated device markers on top (icon and
 * color per kind — see ../mep/markers). HVAC systems overlay duct centerlines
 * (stroke width ∝ duct size, supply/return colors — see ../mep/ducts) and
 * terminal markers (supply diffuser = flat square, return grille = lined
 * square). Clicking a space, a device, or a duct selects it for the side
 * panel.
 */
import { memo } from "react";

import {
  effectiveOccupancy,
  occupancyColor,
  planBounds,
  svgCentroid,
  svgPoints,
  type PlanSpace,
} from "../wizard/plan";
import { markerColor, markerPolygonPoints, markerShape } from "../mep/markers";
import {
  ductColor,
  ductPolylinePoints,
  ductStrokeWidth,
  terminalColor,
  terminalSystem,
} from "../mep/ducts";
import type { MechanicalEquipment, MepDevice, MepDuct, MepTerminal } from "../api/types";
import { MECHANICAL_PLAN_COLOR, mechanicalGlyphHalf } from "../mep/mechanical";

/** Marker outer radius in plan meters. */
const DEVICE_RADIUS = 0.35;
/** Terminal square half-size in plan meters (matches the 3D marker face). */
const TERMINAL_HALF = 0.3;

export interface PlanViewProps {
  spaces: PlanSpace[];
  selectedSpaceId: string | null;
  onSelect: (spaceId: string) => void;
  /** Generated devices to draw on this storey (already filtered by storey). */
  devices?: MepDevice[];
  selectedDeviceId?: string | null;
  onSelectDevice?: (deviceId: string) => void;
  /** space id → zone tint color (synced with the zone legend). */
  zoneTints?: Map<string, string>;
  /** HVAC duct runs to overlay (already filtered by storey). */
  ducts?: MepDuct[];
  selectedDuctId?: string | null;
  onSelectDuct?: (ductId: string) => void;
  /** HVAC terminals to draw (already filtered by storey). */
  terminals?: MepTerminal[];
  /** Placed mechanical equipment to draw (already filtered by storey). */
  mechanicalEquipment?: MechanicalEquipment[];
  selectedMechanicalId?: string | null;
  onSelectMechanical?: (equipmentId: string) => void;
}

/**
 * One placed mechanical element (RTU / AHU / pump / VAV terminal) as a plan
 * glyph: a footprint-scaled rectangle per kind, colored to match the 3D
 * MechanicalLayer glyphs. Mirrors {@link TerminalMarker} / {@link DeviceMarker}
 * — same y-flip, same selection stroke, same click-to-select contract.
 */
const MechanicalMarker = memo(function MechanicalMarker({
  equipment,
  selected,
  onSelect,
}: {
  equipment: MechanicalEquipment;
  selected: boolean;
  onSelect?: (equipmentId: string) => void;
}) {
  // Same y-flip as svgPoints so glyphs land on their host space.
  const cx = equipment.position[0];
  const cy = -equipment.position[1];
  const [hw, hh] = mechanicalGlyphHalf(equipment.kind);
  const color = MECHANICAL_PLAN_COLOR[equipment.kind] ?? "#ffffff";
  return (
    <g
      data-testid="plan-mechanical"
      data-mechanical-id={equipment.id}
      data-mechanical-kind={equipment.kind}
      data-mechanical-tag={equipment.tag ?? equipment.id}
      style={{ cursor: "pointer" }}
      onClick={() => onSelect?.(equipment.id)}
    >
      <rect
        x={cx - hw}
        y={cy - hh}
        width={hw * 2}
        height={hh * 2}
        fill={color}
        fillOpacity={0.85}
        stroke={selected ? "#ffffff" : "#12121e"}
        strokeWidth={selected ? 0.16 : 0.06}
      />
    </g>
  );
});

const DuctPath = memo(function DuctPath({
  duct,
  selected,
  onSelect,
}: {
  duct: MepDuct;
  selected: boolean;
  onSelect?: (ductId: string) => void;
}) {
  const points = ductPolylinePoints(duct.centerline);
  const width = ductStrokeWidth(duct);
  return (
    <g>
      {selected && (
        <polyline
          points={points}
          fill="none"
          stroke="#ffffff"
          strokeWidth={width + 0.14}
          strokeLinecap="round"
          strokeLinejoin="round"
          pointerEvents="none"
        />
      )}
      <polyline
        data-testid="plan-duct"
        data-duct-id={duct.id}
        data-duct-system={duct.system}
        points={points}
        fill="none"
        stroke={ductColor(duct.system)}
        strokeWidth={width}
        strokeOpacity={0.85}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ cursor: "pointer" }}
        onClick={() => onSelect?.(duct.id)}
      />
    </g>
  );
});

const TerminalMarker = memo(function TerminalMarker({ terminal }: { terminal: MepTerminal }) {
  // Same y-flip as svgPoints so terminals land on their spaces.
  const cx = terminal.x_m;
  const cy = -terminal.y_m;
  const color = terminalColor(terminal.kind);
  const lined = terminalSystem(terminal.kind) === "return";
  return (
    <g
      data-testid="plan-terminal"
      data-terminal-id={terminal.id}
      data-terminal-kind={terminal.kind}
    >
      <rect
        x={cx - TERMINAL_HALF}
        y={cy - TERMINAL_HALF}
        width={TERMINAL_HALF * 2}
        height={TERMINAL_HALF * 2}
        fill={lined ? "none" : color}
        fillOpacity={lined ? undefined : 0.9}
        stroke={color}
        strokeWidth={0.07}
      />
      {lined &&
        [-0.5, 0, 0.5].map((offset) => (
          <line
            key={offset}
            x1={cx - TERMINAL_HALF * 0.8}
            x2={cx + TERMINAL_HALF * 0.8}
            y1={cy + offset * TERMINAL_HALF}
            y2={cy + offset * TERMINAL_HALF}
            stroke={color}
            strokeWidth={0.07}
          />
        ))}
    </g>
  );
});

const DeviceMarker = memo(function DeviceMarker({
  device,
  selected,
  onSelect,
}: {
  device: MepDevice;
  selected: boolean;
  onSelect?: (deviceId: string) => void;
}) {
  // Same y-flip as svgPoints so devices land on their spaces.
  const cx = device.x_m;
  const cy = -device.y_m;
  const shape = markerShape(device.kind);
  const common = {
    "data-testid": "plan-device",
    "data-device-id": device.id,
    "data-device-kind": device.kind,
    fill: markerColor(device.kind),
    stroke: selected ? "#ffffff" : "#12121e",
    strokeWidth: selected ? 0.12 : 0.05,
    style: { cursor: "pointer" },
    onClick: () => onSelect?.(device.id),
  } as const;

  if (shape === "circle") {
    return <circle {...common} cx={cx} cy={cy} r={DEVICE_RADIUS} />;
  }
  return <polygon {...common} points={markerPolygonPoints(shape, cx, cy, DEVICE_RADIUS) ?? ""} />;
});

export function PlanView({
  spaces,
  selectedSpaceId,
  onSelect,
  devices = [],
  selectedDeviceId = null,
  onSelectDevice,
  zoneTints,
  ducts = [],
  selectedDuctId = null,
  onSelectDuct,
  terminals = [],
  mechanicalEquipment = [],
  selectedMechanicalId = null,
  onSelectMechanical,
}: PlanViewProps) {
  const drawable = spaces.filter((s) => s.footprint !== null);
  const bounds = planBounds(drawable);

  if (!bounds || drawable.length === 0) {
    return (
      <p data-testid="plan-empty" style={{ color: "#8e8eb8" }}>
        No space footprints on this storey — the model has no derivable space geometry here.
      </p>
    );
  }

  // svgPoints flips y (plan north up), so the viewBox y range flips too.
  const viewBox = `${bounds.minX} ${-(bounds.minY + bounds.height)} ${bounds.width} ${bounds.height}`;

  return (
    <svg
      data-testid="plan-view"
      viewBox={viewBox}
      style={{
        width: "100%",
        height: "100%",
        background: "#12121e",
        borderRadius: 8,
      }}
      preserveAspectRatio="xMidYMid meet"
    >
      {drawable.map(({ space, footprint }) => {
        const selected = space.id === selectedSpaceId;
        const occupancy = effectiveOccupancy(space);
        const zoneTint = zoneTints?.get(space.id);
        const [cx, cy] = svgCentroid(footprint!);
        return (
          <g key={space.id}>
            <polygon
              data-testid="plan-space"
              data-space-id={space.id}
              points={svgPoints(footprint!)}
              fill={occupancyColor(occupancy)}
              fillOpacity={space.exclusion === "no_go" ? 0.25 : 0.55}
              stroke={
                selected
                  ? "#ffffff"
                  : space.is_exit
                    ? "#98c379"
                    : space.exclusion !== "none"
                      ? "#e06c75"
                      : "#555577"
              }
              strokeWidth={selected || space.is_exit ? 0.18 : 0.06}
              strokeDasharray={space.exclusion === "exclusion" ? "0.4 0.25" : undefined}
              style={{ cursor: "pointer" }}
              onClick={() => onSelect(space.id)}
            />
            {zoneTint && (
              <polygon
                data-testid="plan-zone-tint"
                data-space-id={space.id}
                points={svgPoints(footprint!)}
                fill={zoneTint}
                fillOpacity={0.3}
                stroke={zoneTint}
                strokeWidth={0.1}
                pointerEvents="none"
              />
            )}
            <text
              x={cx}
              y={cy}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={0.6}
              fill="#d8d8e8"
              pointerEvents="none"
            >
              {space.is_exit ? `${space.name} · EXIT` : space.name}
            </text>
          </g>
        );
      })}
      {ducts.map((duct) => (
        <DuctPath
          key={duct.id}
          duct={duct}
          selected={duct.id === selectedDuctId}
          onSelect={onSelectDuct}
        />
      ))}
      {terminals.map((terminal) => (
        <TerminalMarker key={terminal.id} terminal={terminal} />
      ))}
      {mechanicalEquipment.map((equipment) => (
        <MechanicalMarker
          key={equipment.id}
          equipment={equipment}
          selected={equipment.id === selectedMechanicalId}
          onSelect={onSelectMechanical}
        />
      ))}
      {devices.map((device) => (
        <DeviceMarker
          key={device.id}
          device={device}
          selected={device.id === selectedDeviceId}
          onSelect={onSelectDevice}
        />
      ))}
    </svg>
  );
}
